/**
 *
 *  Slider with YouTube videos on Lifestyle landing page
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 11/06/2027.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import Slider from "react-slick";

// Components
import SliderPrevArrow from "../../SliderPrevButton/SliderPrevButton";
import SliderNextArrow from "../../SliderNextButton/SliderNextButton";

// Hooks
import useTheme from "../../ThemeProvider/useTheme/useTheme";
import { useIntersectionObserver } from "../../../hooks/useIntersectionObserver/useIntersectionObserver";

// Utils
import { clientWidth } from "../../../utils";

const sliderSettings = {
  dots: true,
  arrows: true,
  infinite: true,
  autoplay: false,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
};

export const YouTubeVideoSlider = ({ videoUrls, onSlideClick }) => {
  const { theme } = useTheme();

  const [height, setHeight] = useState(
    clientWidth() > theme.responsive.breakpoints.xl
      ? 496
      : clientWidth() > theme.responsive.breakpoints.md
      ? 405
      : 205
  );
  const sliderRef = useRef(null);

  const { ref, inView } = useIntersectionObserver(
    {
      triggerOnce: true,
      threshold: 0.1,
      rootMargin: "100px 0px 200px 0px",
    },
    []
  );

  useEffect(() => {
    if (sliderRef.current) {
      setHeight((sliderRef.current.offsetWidth * 9) / 16);
    }
  }, [sliderRef.current]);

  const handleSlideChange = useCallback(() => {
    if (sliderRef.current) {
      const ytPlayerIframes =
        sliderRef.current.querySelectorAll(".yt_player_iframe");
      ytPlayerIframes.forEach((iframe) => {
        iframe.contentWindow.postMessage(
          '{"event":"command","func":"stopVideo","args":""}',
          "*"
        );
      });
    }
  }, [inView]);

  return (
    <div
      ref={(node) => {
        if (node) {
          sliderRef.current = node;
          if (typeof ref === "function") {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        }
      }}
      className="youtube-video-slider"
    >
      {inView && (
        <Slider
          {...sliderSettings}
          className="events-videos-carousel tbk-pb-1 tbk-text-center"
          dotsClass="slick-dots !tbk-inline-block tbk--translate-x-1/2 tbk-left-1/2"
          arrows={clientWidth() > theme.responsive.breakpoints.lg}
          pauseOnFocus
          pauseOnDotsHover
          pauseOnHover
          nextArrow={
            <SliderNextArrow buttonClassName="tbk-h-[48px] tbk-w-[48px]" />
          }
          prevArrow={
            <SliderPrevArrow buttonClassName="tbk-h-[48px] tbk-w-[48px]" />
          }
          afterChange={handleSlideChange}
        >
          {videoUrls.map((url, index) => (
            <div
              key={index}
              id={`slide-${index}`}
              className="slide partner-review-item"
              onClick={() => onSlideClick(index)}
            >
              <iframe
                className="yt_player_iframe tbk-overflow-hidden tbk-rounded-2xl"
                width="100%"
                height={height}
                src={url}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                // referrerPolicy="strict-origin-when-cross-origin"
              />
            </div>
          ))}
        </Slider>
      )}
    </div>
  );
};

export default YouTubeVideoSlider;
